// Replay viewer overlay (REP-4). A self-contained DOM control surface bound to a
// pure `ReplayPlayer`: a timeline scrubber, play/pause, the REP_SPEEDS speed set,
// jump-to-keyframe, a spectator-camera toggle, a live read-out of the replay state
// (tick / player XYZ / world hash), a timelapse PNG-sequence export, and the
// always-on record dot. It owns its own play clock (rAF) so scrubbing is independent
// of the game tick.
//
// The overlay drives the deterministic replay engine AND, through an injected
// `ReplaySceneView`, re-renders the replay world in 3D: opening it attaches the
// player to the view (the main render loop then draws the replay scene instead of the
// live one), closing it detaches. The view stays behind an interface so the overlay
// keeps importing no three.js and remains unit-testable in jsdom (pass no view).
import { REP } from '../../sim/config';
import type { ReplayPlayer } from '../../sim/replay/player';
import { ReplayDriftError } from '../../sim/replay/player';
import type { ReplaySceneView } from '../render/replayview';

/** Capture the current rendered frame as a PNG data URL (the client screenshot hook,
 *  UI-9). Injected so the overlay stays DOM-testable without a real WebGL context. */
export type ScreenshotHook = () => string;

export class ReplayOverlay {
  readonly root: HTMLElement;
  readonly dot: HTMLElement;
  private readonly scrub: HTMLInputElement;
  private readonly readout: HTMLElement;
  private readonly speedLabel: HTMLElement;
  private player: ReplayPlayer | null = null;
  private playing = false;
  private speed = 1;
  private raf = 0;
  private lastMs = 0;
  private accum = 0;
  private shot: ScreenshotHook;
  private readonly view: ReplaySceneView | null;
  /** Timelapse frames captured on the last export (data URLs). */
  timelapse: string[] = [];

  constructor(
    shot: ScreenshotHook = () => '',
    view: ReplaySceneView | null = null,
    /** Hangar §39.5 (M105.1) — "scrubber Save replay". Injected so the overlay stays
     *  DOM-testable with no `Game`/bridge: the controller supplies the real save. */
    private readonly onSaveReplay: () => void = () => undefined,
  ) {
    this.shot = shot;
    this.view = view;
    this.root = el('div', 'bb-replay-overlay');
    this.root.style.display = 'none';
    this.root.dataset.testid = 'replay-overlay';

    const bar = el('div', 'bb-replay-bar');
    const playBtn = btn('bb-replay-play', '▶', () => this.togglePlay());
    playBtn.dataset.testid = 'replay-play';
    this.scrub = document.createElement('input');
    this.scrub.type = 'range';
    this.scrub.className = 'bb-replay-scrub';
    this.scrub.dataset.testid = 'replay-scrub';
    this.scrub.min = '0';
    this.scrub.addEventListener('input', () => { if (this.player) this.seekTo(Number(this.scrub.value)); });

    this.speedLabel = el('span', 'bb-replay-speed-label');
    const speedBtn = btn('bb-replay-speed', '1×', () => this.cycleSpeed());
    speedBtn.dataset.testid = 'replay-speed';
    const kfBtn = btn('bb-replay-keyframe', '⏮ key', () => this.jumpKeyframe());
    const camBtn = btn('bb-replay-cam', 'cam', () => this.view?.cycleCam());
    camBtn.dataset.testid = 'replay-cam';
    const tlBtn = btn('bb-replay-timelapse', 'timelapse', () => this.exportTimelapse());
    tlBtn.dataset.testid = 'replay-timelapse';
    const saveBtn = btn('bb-replay-save', 'Save replay', () => this.onSaveReplay());
    saveBtn.dataset.testid = 'replay-save';
    const closeBtn = btn('bb-replay-close', '✕', () => this.close());
    closeBtn.dataset.testid = 'replay-close';

    this.readout = el('div', 'bb-replay-readout');
    this.readout.dataset.testid = 'replay-readout';

    bar.append(playBtn, this.scrub, speedBtn, this.speedLabel, kfBtn, camBtn, tlBtn, saveBtn, closeBtn);
    this.root.append(bar, this.readout);

    // The record dot lives outside the overlay (it shows during live recording, UI-4).
    this.dot = el('div', 'bb-record-dot');
    this.dot.dataset.testid = 'record-dot';
    this.dot.style.display = 'none';
    this.dot.textContent = '● REC';

    this.updateSpeedLabel();
  }

  /** Mount the overlay + record dot into the page (idempotent). */
  mount(parent: HTMLElement = document.body): void {
    if (!this.root.isConnected) parent.append(this.root);
    if (!this.dot.isConnected) parent.append(this.dot);
  }

  /** Show/hide the "● REC" dot (driven by the live recorder, REP-1/UI-4). */
  setRecording(on: boolean): void {
    this.dot.style.display = on ? 'block' : 'none';
  }

  get isOpen(): boolean {
    return this.root.style.display !== 'none';
  }

  /** Open the viewer on a replay player (read-only; REP-3). Attaches the player to
   *  the 3D view so the main loop starts rendering the replay world. */
  open(player: ReplayPlayer): void {
    this.player = player;
    this.playing = false;
    this.scrub.min = String(player.startTick);
    this.scrub.max = String(player.endTick);
    this.scrub.value = String(player.tick);
    this.root.style.display = 'block';
    this.view?.attach(player);
    this.refresh();
  }

  close(): void {
    this.stopClock();
    this.player = null;
    this.view?.detach(); // hand rendering back to the live world
    this.root.style.display = 'none';
  }

  private seekTo(tick: number): void {
    if (!this.player) return;
    try { this.player.seek(tick); } catch (e) { this.reportDrift(e); return; }
    this.refresh();
  }

  private jumpKeyframe(): void {
    if (!this.player) return;
    try { this.player.seekToKeyframe(this.player.tick - 1); } catch (e) { this.reportDrift(e); return; }
    this.refresh();
  }

  togglePlay(): void {
    if (!this.player) return;
    this.playing = !this.playing;
    if (this.playing) this.startClock(); else this.stopClock();
  }

  private cycleSpeed(): void {
    const i = REP.SPEEDS.indexOf(this.speed as (typeof REP.SPEEDS)[number]);
    this.speed = REP.SPEEDS[(i + 1) % REP.SPEEDS.length]!;
    this.updateSpeedLabel();
  }

  private updateSpeedLabel(): void {
    this.speedLabel.textContent = `${this.speed}×`;
  }

  /** Export a timelapse: re-sim start→end sampling a screenshot every N ticks
   *  (REP-4). Returns the captured PNG data URLs (also stored on `.timelapse`). */
  exportTimelapse(everyN = REP.KEYFRAME_TICKS): string[] {
    if (!this.player) return [];
    this.timelapse = [];
    try {
      this.player.runTimelapse(everyN, () => { this.timelapse.push(this.shot()); });
    } catch (e) { this.reportDrift(e); return this.timelapse; }
    this.refresh();
    return this.timelapse;
  }

  private startClock(): void {
    this.lastMs = performance.now();
    this.accum = 0;
    const loop = (now: number): void => {
      if (!this.playing || !this.player) return;
      const dt = (now - this.lastMs) / 1000;
      this.lastMs = now;
      this.accum += dt * 20 * this.speed; // 20 TPS × speed
      let advanced = false;
      while (this.accum >= 1) {
        this.accum -= 1;
        let ok: boolean;
        try { ok = this.player.stepForward(); } catch (e) { this.reportDrift(e); return; }
        advanced = true;
        if (!ok) { this.playing = false; break; }
      }
      if (advanced) this.refresh();
      if (this.playing) this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  private stopClock(): void {
    this.playing = false;
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
  }

  /** E-31: a drift stops playback and surfaces the diverging tick loudly. */
  private reportDrift(e: unknown): void {
    this.stopClock();
    if (e instanceof ReplayDriftError) {
      this.readout.classList.add('bb-replay-drift');
      this.readout.textContent = `⚠ replay drift at tick ${e.tick} — determinism bug (playback stopped)`;
    } else {
      throw e;
    }
  }

  private refresh(): void {
    if (!this.player) return;
    const p = this.player;
    this.scrub.value = String(p.tick);
    const pose = p.playerPose();
    this.readout.classList.remove('bb-replay-drift');
    this.readout.textContent =
      `tick ${p.tick}/${p.endTick}  ·  xyz ${pose.x.toFixed(1)} ${pose.y.toFixed(1)} ${pose.z.toFixed(1)}  ·  ${p.hash().slice(0, 8)}`;
  }
}

function el(tag: string, cls: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = cls;
  return e;
}
function btn(cls: string, label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = cls;
  b.textContent = label;
  b.addEventListener('click', onClick);
  return b;
}
